import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { adminNotificationService } from "../../Api/Services/adminNotificationService";
import { useAdminNotifications } from "../../Contexts/AdminNotificationContext";
import { AdminNotificationCategory } from "../../Enums/AdminNotificationCategory";
import { AdminNotification } from "../../Models/AdminNotification";
import AdminNotificationItem, { ADMIN_NOTIFICATION_CATEGORY_LABELS } from "../AdminNotificationItem/AdminNotificationItem";
import Pagination from "../Pagination/Pagination";

const PAGE_SIZE = 10;

const CATEGORY_FILTERS: { label: string; value: AdminNotificationCategory | null }[] = [
  { label: "Todas", value: null },
  ...[
    AdminNotificationCategory.Order,
    AdminNotificationCategory.Customer,
    AdminNotificationCategory.Support,
    AdminNotificationCategory.Product
  ].map((value) => ({ label: ADMIN_NOTIFICATION_CATEGORY_LABELS[value], value }))
];

const AdminNotificationPanel = () => {
  const location = useLocation();
  const { unreadCount, version, markAllAsRead } = useAdminNotifications();
  const sectionRef = useRef<HTMLElement>(null);

  const [category, setCategory] = useState<AdminNotificationCategory | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Refetch on page/filter change and whenever the shared state changes
  // (new notification, item read, all read)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const page = await adminNotificationService.getAll({ page: currentPage, pageSize: PAGE_SIZE, category });
        if (!cancelled) {
          setNotifications(page.items);
          setTotalPages(Math.max(page.totalPages, 1));
        }
      } catch {
        // Keep showing the last page we had
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, category, version]);

  useEffect(() => {
    if (location.hash === "#notificacoes") {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  const handleCategoryChange = (value: AdminNotificationCategory | null) => {
    setCategory(value);
    setCurrentPage(1);
  };

  return (
    <section id="notificacoes" ref={sectionRef} className="mt-8 border bg-white shadow-sm rounded scroll-mt-6">
      <div className="flex flex-col gap-3 bg-primary-120 px-4 py-4 border-b sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">
          {"Notificações"}
          {unreadCount > 0 && (
            <span className="ml-2 align-middle text-xs font-bold text-white bg-primary-600 rounded-full px-2 py-0.5">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => void markAllAsRead()}
            className="self-start text-sm text-primary-600 hover:text-primary-700 hover:underline sm:self-auto"
          >
            {"Marcar todas como lidas"}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 px-4 py-3 border-b">
        {CATEGORY_FILTERS.map(({ label, value }) => (
          <button
            key={label}
            type="button"
            onClick={() => handleCategoryChange(value)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              category === value
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white text-gray-700 border-gray-300 hover:bg-primary-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && notifications.length === 0 ? (
        <div className="divide-y">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex gap-3 px-4 py-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 bg-gray-200 rounded" />
                <div className="h-3 w-2/3 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-gray-500">
          {category === null
            ? "Nenhuma notificação por enquanto. Novos pedidos, pagamentos, cadastros e mensagens aparecem aqui."
            : "Nenhuma notificação nesta categoria."}
        </p>
      ) : (
        <div className="divide-y">
          {notifications.map((notification) => (
            <AdminNotificationItem key={notification.id} notification={notification} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pb-6">
          <Pagination
            className="!mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </section>
  );
};

export default AdminNotificationPanel;
